import { Link } from 'react-router-dom';
import { Plus, FolderKanban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useProjects } from '@/hooks/use-projects';
import { useUIStore } from '@/stores/ui-store';
import { useAuth } from '@/hooks/use-auth';

export function ProjectsPage() {
  const { user } = useAuth();
  const { data: projects = [], isLoading } = useProjects();
  const { setCreateProjectOpen } = useUIStore();
  const isClient = user?.role === 'client';

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Projects</h1>
          <p className="text-muted-foreground">
            {isClient ? 'View your assigned projects' : 'Manage your projects and tasks'}
          </p>
        </div>
        {!isClient && (
          <Button onClick={() => setCreateProjectOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            New Project
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : projects.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <FolderKanban className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">No projects yet</h3>
            <p className="text-muted-foreground text-center mb-6 max-w-md">
              {isClient
                ? 'You have not been assigned to any projects yet.'
                : 'Projects help you organize tasks and collaborate with your team. Create your first project to get started.'}
            </p>
            {!isClient && (
              <Button onClick={() => setCreateProjectOpen(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                Create your first project
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => (
            <Link key={project.id} to={`/projects/${project.id}`}>
              <Card className="hover:border-primary transition-colors cursor-pointer h-full">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div
                      className="h-4 w-4 rounded shrink-0"
                      style={{ backgroundColor: project.color }}
                    />
                    <CardTitle className="text-base truncate">{project.name}</CardTitle>
                  </div>
                  <CardDescription className="line-clamp-2">
                    {project.description || 'No description'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>{project._count?.tasks || 0} tasks</span>
                    <span>{project._count?.members || 0} members</span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
